/**
 * Book search hooks — the boundary between UI and openLibraryService. No
 * component should import openLibraryService directly; everything goes
 * through here so the data source stays swappable (see services/openLibraryService.js).
 * This includes resolving a real cover for a book ScribbleReads already
 * knows the title/author of (seed/mock data) — see useBookCoverLookup below
 * — not just live user-typed search.
 */

import { useMemo } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { getTrendingBooks, searchBooks } from "@/services/openLibraryService";
import { useAuth } from "@/lib/useAuth";
import { useDebouncedValue } from "./useDebouncedValue";

const QUICK_LIMIT = 6;
const PAGE_LIMIT = 20;

export function useTrendingBooks() {
  const { accessToken } = useAuth();
  const result = useQuery({
    queryKey: ["books-trending", accessToken],
    queryFn: ({ signal }) => getTrendingBooks({ limit: 20, signal, accessToken }),
    staleTime: 60_000,
  });

  return {
    books: result.data ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
    refetch: result.refetch,
  };
}

/**
 * Small, single-page search for the live dropdown preview under the
 * Explore search bar. Debounced ~400ms; react-query dedupes/caches by
 * query text and cancels stale requests via the AbortSignal it passes in.
 */
export function useBookQuickSearch(rawQuery) {
  const { accessToken } = useAuth();
  const query = useDebouncedValue(rawQuery.trim(), 400);

  const result = useQuery({
    queryKey: ["books-search-quick", query, accessToken],
    queryFn: ({ signal }) => searchBooks(query, { limit: QUICK_LIMIT, signal, accessToken }),
    enabled: query.length > 0,
    staleTime: 60_000,
  });

  return {
    books: result.data?.books ?? [],
    numFound: result.data?.numFound ?? 0,
    isLoading: result.isFetching && query.length > 0,
    isError: result.isError,
  };
}

/**
 * Full paginated/infinite-scroll search results for the dedicated results
 * view (BookSearchResults). Same debounce + cancellation behavior as the
 * quick search above, just paged.
 */
export function useBookSearch(rawQuery) {
  const { accessToken } = useAuth();
  const query = useDebouncedValue(rawQuery.trim(), 400);

  const result = useInfiniteQuery({
    queryKey: ["books-search", query, accessToken],
    queryFn: ({ pageParam = 1, signal }) => searchBooks(query, { page: pageParam, limit: PAGE_LIMIT, signal, accessToken }),
    enabled: query.length > 0,
    staleTime: 60_000,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  });

  const books = useMemo(
    () => result.data?.pages.flatMap((p) => p.books) ?? [],
    [result.data]
  );
  const numFound = result.data?.pages[0]?.numFound ?? 0;

  return {
    query,
    books,
    numFound,
    isLoading: result.isFetching && !result.isFetchingNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    isError: result.isError,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    refetch: result.refetch,
  };
}

/**
 * Resolves a real Open Library cover/metadata for a book ScribbleReads
 * already knows the title/author of — e.g. the hardcoded-but-real seed
 * titles in Bookshelf/Home/Communities mock data, which carry no
 * `coverUrl` of their own. Unlike the live-typed search above, this is a
 * fixed, rarely-changing lookup (the same handful of titles get requested
 * repeatedly across every page), so it's cached indefinitely for the
 * session (`staleTime: Infinity`) instead of the 60s used for search-as-
 * you-type — one lookup per unique title+author for the whole session, not
 * once per render or per mount.
 */
const COVER_LOOKUP_TIMEOUT_MS = 8000;

export function useBookCoverLookup({ title, author, enabled = true } = {}) {
  const query = author ? `${title} ${author}` : title ?? "";

  const result = useQuery({
    queryKey: ["openlibrary-cover-lookup", title ?? null, author ?? null],
    queryFn: async ({ signal }) => {
      // Bounded so a slow/unreachable network fails fast to the branded
      // gradient fallback instead of leaving a skeleton up indefinitely —
      // this is a nice-to-have enrichment, not critical-path content.
      const controller = new AbortController();
      const onAbort = () => controller.abort();
      signal.addEventListener("abort", onAbort);
      const timeoutId = setTimeout(() => controller.abort(), COVER_LOOKUP_TIMEOUT_MS);
      try {
        return await searchBooks(query, { limit: 1, signal: controller.signal });
      } finally {
        clearTimeout(timeoutId);
        signal.removeEventListener("abort", onAbort);
      }
    },
    enabled: enabled && !!title,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    book: result.data?.books?.[0] ?? null,
    isLoading: result.isFetching,
    isError: result.isError,
  };
}
